import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Mail, CreditCard, Users, Save, TestTube, 
  Eye, EyeOff, CheckCircle, XCircle, Loader2, BarChart3,
  Shield, AlertTriangle, Activity, Database, Server,
  UserCog, Trash2, Ban, Search, RefreshCw,
  HelpCircle, Clock, TrendingUp, Zap, DollarSign,
  Package, Plus, Edit, Power, Calendar, Percent,
  ArrowUpRight, ArrowDownRight, FileText, Upload, Download, Book,
  MessageSquare, Filter, Send, ChevronDown, ChevronUp, PieChart, Share2,
  Newspaper, Globe, Star, UserPlus, Crown, Megaphone, KeyRound, Plug
} from 'lucide-react';
import api from '@/services/api';
import { HelpButton } from '@/components/common/HelpButton';
import { AdminResellersTab } from '@/components/admin/AdminResellersTab';
import { AdminSmtpTab } from '@/components/admin/AdminSmtpTab';
import { AdminStripeTab } from '@/components/admin/AdminStripeTab';
import { AdminUsersTab } from '@/components/admin/AdminUsersTab';
import { AdminPlansTab } from '@/components/admin/AdminPlansTab';
import { AdminTabBoundary } from '@/components/admin/AdminTabBoundary';
import { AdminOverviewTab } from '@/components/admin/AdminOverviewTab';
import { AdminAgentSearch } from '@/components/admin/AdminAgentSearch';
import { AdminContentTab } from '@/components/admin/AdminContentTab';
import { AdminRolesTab, PermissionMatrix, SEGMENT_LABELS, emptyPerms } from '@/components/admin/AdminRolesTab';
import { AdminIntegrationsTab } from '@/components/admin/AdminIntegrationsTab';
import { AdminSupportTab } from '@/components/admin/AdminSupportTab';
import { AdminSeoAbTab } from '@/components/admin/AdminSeoAbTab';
import { AdminSystemHealthTab } from '@/components/admin/AdminSystemHealthTab';
import AdminReportsTab from '@/components/admin/AdminReportsTab';
import { PlanModal } from '@/components/admin/PlanModal';
import { RoleChangeWarningModal } from '@/components/admin/RoleChangeWarningModal';
import { PricingConfigPanel } from '@/components/admin/PricingConfigPanel';
import { rbacAPI } from '@/services/api';

// Highlight a substring match inside a value (case-insensitive) by wrapping
// matched chunks in a subtle yellow <mark>. Used for the Admin → Users
// search results so partial matches are visually obvious.
const highlightMatch = (text, query) => {
  const t = text == null ? '' : String(text);
  const q = (query || '').trim();
  if (!q) return t;
  try {
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = t.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((chunk, i) =>
      chunk.toLowerCase() === q.toLowerCase()
        ? <mark key={`hl-${i}-${chunk}`} className="bg-yellow-200 text-slate-900 rounded px-0.5">{chunk}</mark>
        : <React.Fragment key={`fr-${i}-${chunk.slice(0, 8)}`}>{chunk}</React.Fragment>
    );
  } catch {
    return t;
  }
};

export const AdminPage = () => {
  const { user, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  
  // SMTP Config
  const [smtpConfig, setSmtpConfig] = useState({
    host: '', port: 587, username: '', password: '',
    from_email: '', from_name: 'Harmonix', use_tls: true, use_ssl: false
  });
  const [existingSmtpConfig, setExistingSmtpConfig] = useState(null);
  
  // Paddle Config (replaced Stripe)
  const [paddleConfig, setPaddleConfig] = useState({
    vendor_id: '', api_key: '', webhook_secret: '',
    is_sandbox: true, platform_fee_percent: 5.0
  });
  const [existingPaddleConfig, setExistingPaddleConfig] = useState(null);
  
  // Stats & Health
  const [stats, setStats] = useState(null);
  const [revenueStats, setRevenueStats] = useState(null);
  
  // Users Management
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: '', email: '', password: '', role: 'user', tier: 'free' });
  const [addingUser, setAddingUser] = useState(false);
  
  // Payments
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  
  // Plans
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  // Roles list for the Users-tab role selector (iter130: previously the
  // dropdown was hardcoded and Moderator was missing after the RBAC refactor).
  const [roles, setRoles] = useState([]);
  // iter131 — Role Change Warning: shown when admin picks a non-moderator
  // role for a user who still holds active moderator seats on one or more
  // chatbots. Structure: { open, userId, roleId, seats, userLabel }.
  const [roleChangePrompt, setRoleChangePrompt] = useState({
    open: false, userId: null, roleId: null, seats: [], userLabel: '',
  });
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [suggestedPlanData, setSuggestedPlanData] = useState(null);
  
  // Reseller Settings
  const [resellerSettings, setResellerSettings] = useState({
    default_commission_percent: 20,
    minimum_payout: 100,
    disbursement_day: 1,
    disbursement_time: '09:00'
  });
  
  // Social Media Integration Settings
  const [socialConfig, setSocialConfig] = useState({
    meta_app_id: '',
    meta_app_secret: '',
    meta_access_token: '',
    whatsapp_phone_id: '',
    whatsapp_business_id: '',
    instagram_account_id: '',
    facebook_page_id: '',
    webhook_verify_token: ''
  });
  // Tracks which secret fields are already saved on the server (server never returns the actual secret)
  const [socialConfigured, setSocialConfigured] = useState({
    meta_app_secret_configured: false,
    meta_access_token_configured: false,
  });
  const [savingSocialConfig, setSavingSocialConfig] = useState(false);
  
  // Email logs
  const [emailLogs, setEmailLogs] = useState([]);
  const [testEmail, setTestEmail] = useState('');
  
  // Documentation / User Manual
  const [manualConfig, setManualConfig] = useState(null);
  const [uploadingManual, setUploadingManual] = useState(false);
  const [manualVersion, setManualVersion] = useState('1.0.0');
  
  // Smart Pricing Calculator
  const [smartPricing, setSmartPricing] = useState(null);
  const [smartPricingLoading, setSmartPricingLoading] = useState(false);
  const [smartPricingData, setSmartPricingData] = useState(null);
  const [targetProfitMargin, setTargetProfitMargin] = useState(30);
  
  // Support & Feedback Analytics
  const [supportStats, setSupportStats] = useState(null);
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportFilter, setSupportFilter] = useState({ type: '', status: '', category: '' });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketResponse, setTicketResponse] = useState('');
  const [updatingTicket, setUpdatingTicket] = useState(false);
  
  // Contact Form Submissions
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactReply, setContactReply] = useState('');
  const [sendingContactReply, setSendingContactReply] = useState(false);
  
  // Subscription editing
  const [editingUserSub, setEditingUserSub] = useState(null);

  // RBAC — role list (for assigning to users) + per-user overrides editor
  const [rbacRoles, setRbacRoles] = useState([]);
  const [rbacSegments, setRbacSegments] = useState([]);
  const [rbacActions, setRbacActions] = useState(['read', 'write', 'edit', 'delete']);
  const [rbacGroups, setRbacGroups] = useState([]);
  const [editingPermsUser, setEditingPermsUser] = useState(null); // user being edited
  const [overrideForm, setOverrideForm] = useState({});
  const [savingOverrides, setSavingOverrides] = useState(false);

  // Bulk selection (Users tab)
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [bulkRoleId, setBulkRoleId] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [newUserTier, setNewUserTier] = useState('');
  const [savingSubscription, setSavingSubscription] = useState(false);
  
  // Plan migration — owned by AdminPlansTab which renders its own dialog
  // (data-testid="plan-migration-dialog"). AdminPage no longer needs any of
  // this state; kept previously as a leftover from the pre-extraction era.
  
  // Case Studies / Blogs
  const [caseStudies, setCaseStudies] = useState([]);
  const [caseStudiesStats, setCaseStudiesStats] = useState(null);
  const [caseStudiesLoading, setCaseStudiesLoading] = useState(false);
  const [showCaseStudyModal, setShowCaseStudyModal] = useState(false);
  const [editingCaseStudy, setEditingCaseStudy] = useState(null);
  const [caseStudyForm, setCaseStudyForm] = useState({
    title: '',
    summary: '',
    content: '',
    industry: '',
    company_name: '',
    featured_image: '',
    tags: '',
    post_type: 'case_study',
    is_published: false,
    is_featured: false,
    results: []
  });
  const [savingCaseStudy, setSavingCaseStudy] = useState(false);
  
  const isSystemAdmin = user?.role === 'system_admin';

  // RBAC: a tab is visible if user is system_admin OR has 'read' permission on its segment.
  const canSee = (segment) => isSystemAdmin || hasPermission(segment, 'read');
  // Any admin tab the current user can access at all?
  const canSeeAnyAdminTab = isSystemAdmin || [
    'overview', 'users', 'plans', 'payments', 'resellers', 'content', 'docs',
    'support', 'smtp', 'stripe', 'social', 'roles'
  ].some((s) => hasPermission(s, 'read'));
  
  useEffect(() => {
    if (isSystemAdmin || canSeeAnyAdminTab) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [isSystemAdmin, canSeeAnyAdminTab]);
  
  const loadData = async () => {
    try {
      const [smtpRes, paddleRes, statsRes, logsRes, revenueRes, resellerRes, manualRes, rolesRes, segRes, socialRes] = await Promise.all([
        api.get('/admin/smtp').catch(() => ({ data: null })),
        api.get('/admin/paddle').catch(() => ({ data: null })),
        api.get('/admin/stats').catch(() => ({ data: null })),
        api.get('/admin/email-logs?limit=20').catch(() => ({ data: { logs: [] } })),
        api.get('/admin/revenue').catch(() => ({ data: null })),
        api.get('/admin/reseller-settings').catch(() => ({ data: null })),
        api.get('/admin/user-manual').catch(() => ({ data: null })),
        rbacAPI.listRoles().catch(() => ({ data: { roles: [] } })),
        rbacAPI.getSegments().catch(() => ({ data: { segments: [], actions: ['read', 'write', 'edit', 'delete'] } })),
        api.get('/admin/config/social').catch(() => ({ data: null })),
      ]);
      
      if (smtpRes.data) {
        setExistingSmtpConfig(smtpRes.data);
        setSmtpConfig({
          host: smtpRes.data.host || '', port: smtpRes.data.port || 587,
          username: smtpRes.data.username || '', password: '',
          from_email: smtpRes.data.from_email || '', from_name: smtpRes.data.from_name || 'Harmonix',
          use_tls: smtpRes.data.use_tls ?? true, use_ssl: smtpRes.data.use_ssl ?? false
        });
      }
      
      if (paddleRes.data) {
        setExistingPaddleConfig(paddleRes.data);
        setPaddleConfig({
          vendor_id: paddleRes.data.vendor_id || '',
          api_key: '',
          webhook_secret: '',
          is_sandbox: paddleRes.data.is_sandbox ?? true,
          platform_fee_percent: paddleRes.data.platform_fee_percent || 5.0
        });
      }
      
      if (statsRes.data) setStats(statsRes.data);
      if (logsRes.data) setEmailLogs(logsRes.data.logs || []);
      if (revenueRes.data) setRevenueStats(revenueRes.data);
      if (resellerRes.data) setResellerSettings(resellerRes.data);
      if (manualRes.data) {
        setManualConfig(manualRes.data);
        setManualVersion(manualRes.data.version || '1.0.0');
      }
      if (rolesRes?.data?.roles) setRbacRoles(rolesRes.data.roles);
      if (segRes?.data?.segments) {
        setRbacSegments(segRes.data.segments);
        setRbacActions(segRes.data.actions || ['read', 'write', 'edit', 'delete']);
        if (Array.isArray(segRes.data.groups)) setRbacGroups(segRes.data.groups);
      }

      // Social Media config — hydrate non-secret fields so the form shows what's already saved
      if (socialRes?.data?.configured) {
        setSocialConfig((prev) => ({
          ...prev,
          meta_app_id: socialRes.data.meta_app_id || '',
          whatsapp_phone_id: socialRes.data.whatsapp_phone_id || '',
          whatsapp_business_id: socialRes.data.whatsapp_business_id || '',
          instagram_account_id: socialRes.data.instagram_account_id || '',
          facebook_page_id: socialRes.data.facebook_page_id || '',
          webhook_verify_token: socialRes.data.webhook_verify_token || '',
        }));
        setSocialConfigured({
          meta_app_secret_configured: !!socialRes.data.meta_app_secret_configured,
          meta_access_token_configured: !!socialRes.data.meta_access_token_configured,
        });
      }
      
    } catch (error) {
      console.error('Failed to load admin data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await api.get(`/admin/users?search=${userSearch}`);
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  // iter130: Load RBAC roles so the Users tab can present the actual role
  // list (including Moderator + any custom roles) in the assignment dropdown
  // instead of the hardcoded 3-item list that lived in the modal before.
  const loadRolesList = async () => {
    try {
      const res = await rbacAPI.listRoles();
      setRoles(res.data?.roles || []);
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  // RBAC: change a user's role from the Users table
  // Warns before demoting a user who still holds active moderator seats,
  // then delegates the actual role change to _performRoleChange below.
  // iter131 fix — previous UX silently changed the role and the demoted
  // user kept access to the chatbots they were moderating.
  const handleAssignRole = async (userId, roleId) => {
    // Only care about seats when the target role is NOT moderator/admin.
    // Promoting stays a one-click op.
    const rolePreservesSeats = roleId === 'role_moderator' || roleId === 'role_system_admin';
    if (!rolePreservesSeats) {
      try {
        const res = await api.get(`/admin/users/${userId}/moderator-seats`);
        const seats = res.data?.seats || [];
        if (seats.length > 0) {
          setRoleChangePrompt({
            open: true,
            userId,
            roleId,
            seats,
            userLabel: users.find((u) => u.user_id === userId)?.email || userId,
          });
          return; // Wait for the confirm-dialog action
        }
      } catch (err) {
        // If the check itself fails we still let the admin proceed —
        // otherwise a flaky endpoint would block every role change. Show
        // a soft warning toast so the admin at least knows the safety
        // check couldn't run (reviewer feedback iter131).
        toast.warning(
          'Could not verify moderator seats — role will change without a seat audit'
        );
      }
    }
    return _performRoleChange(userId, roleId);
  };

  const _performRoleChange = async (userId, roleId) => {
    try {
      await rbacAPI.assignUserRole(userId, roleId);
      toast.success('Role updated');
      // Optimistically update the row. Store the raw role_id so the
      // <Select value={u.role}> stays in sync with its <SelectItem
      // value={r.role_id}> children — writing the legacy short name
      // ('user'/'system_admin') caused the trigger to render blank
      // until reload (iter134 regression).
      setUsers((prev) => prev.map((u) => (
        u.user_id === userId ? { ...u, role: roleId } : u
      )));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  // RBAC: open per-user permission overrides modal
  const openPermsOverride = async (u) => {
    try {
      const res = await rbacAPI.getUserPermissions(u.user_id);
      setEditingPermsUser({ ...u, _effective: res.data.effective_permissions, _role: res.data.role });
      // Prefill the matrix with the user's CURRENT effective permissions
      // (role defaults merged with any existing overrides). This makes
      // "Admin" users show up as all-checked instead of all "None", which
      // was the misleading UX in the previous implementation. Toggling
      // any checkbox will then explicitly write an override for that
      // segment/action.
      const existingOverrides = res.data.permission_overrides || {};
      const effective = res.data.effective_permissions || {};
      const seed = {};
      Object.keys(effective).forEach((seg) => {
        const overrideRow = existingOverrides[seg] || {};
        seed[seg] = {};
        Object.keys(effective[seg] || {}).forEach((act) => {
          // Prefer explicit override, otherwise fall back to effective.
          seed[seg][act] = act in overrideRow ? !!overrideRow[act] : !!effective[seg][act];
        });
      });
      setOverrideForm(seed);
    } catch (err) {
      toast.error('Failed to load permissions');
    }
  };

  const saveOverrides = async () => {
    if (!editingPermsUser) return;
    // System Admin permissions cannot be overridden — the backend
    // early-returns True for role="system_admin" in user_has_permission,
    // and by design super-admin can never be locked out. Refuse the
    // save and surface a clear message.
    if (editingPermsUser._role === 'system_admin' || editingPermsUser._role === 'role_system_admin') {
      toast.error('System Admin permissions cannot be overridden. This role always has full access.');
      return;
    }
    setSavingOverrides(true);
    try {
      // Only persist values that differ from the user's role default —
      // otherwise we'd store "the role gives me admin.read=true and I
      // set it to true" which pollutes the DB and can't be distinguished
      // from a real intentional override during role-change flows.
      const roleDefault = editingPermsUser._effective || {};
      const cleaned = {};
      Object.keys(overrideForm || {}).forEach((seg) => {
        const row = overrideForm[seg] || {};
        const rolRow = roleDefault[seg] || {};
        const delta = {};
        Object.keys(row).forEach((act) => {
          if (!!row[act] !== !!rolRow[act]) delta[act] = !!row[act];
        });
        if (Object.keys(delta).length) cleaned[seg] = delta;
      });
      await rbacAPI.setUserOverrides(editingPermsUser.user_id, cleaned);
      toast.success(
        Object.keys(cleaned).length
          ? `Permissions updated (${Object.keys(cleaned).length} segment${Object.keys(cleaned).length > 1 ? 's' : ''} overridden)`
          : 'All permissions match the role defaults — no overrides saved.'
      );
      setEditingPermsUser(null);
      setOverrideForm({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save overrides');
    } finally {
      setSavingOverrides(false);
    }
  };

  const clearOverrides = async () => {
    if (!editingPermsUser) return;
    if (!window.confirm('Remove all per-user overrides? User will inherit role permissions only.')) return;
    setSavingOverrides(true);
    try {
      await rbacAPI.setUserOverrides(editingPermsUser.user_id, null);
      toast.success('Overrides cleared');
      setEditingPermsUser(null);
      setOverrideForm({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear overrides');
    } finally {
      setSavingOverrides(false);
    }
  };

  // Bulk select handlers
  const toggleSelectUser = (userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const selectable = users.filter((u) => u.role !== 'system_admin').map((u) => u.user_id);
    setSelectedUserIds((prev) => {
      if (selectable.every((id) => prev.has(id)) && selectable.length > 0) {
        return new Set();
      }
      return new Set(selectable);
    });
  };

  const handleBulkAssignRole = async () => {
    if (selectedUserIds.size === 0) { toast.error('Select at least one user'); return; }
    if (!bulkRoleId) { toast.error('Pick a role to assign'); return; }
    const role = rbacRoles.find((r) => r.role_id === bulkRoleId);
    if (!window.confirm(`Assign role "${role?.name || bulkRoleId}" to ${selectedUserIds.size} user${selectedUserIds.size === 1 ? '' : 's'}?`)) return;
    setBulkAssigning(true);
    try {
      const ids = Array.from(selectedUserIds);
      const res = await rbacAPI.bulkAssignRole(ids, bulkRoleId);
      toast.success(`Updated ${res.data.modified_count} user${res.data.modified_count === 1 ? '' : 's'}`);
      const storedRole = bulkRoleId === 'role_system_admin' ? 'system_admin'
                       : bulkRoleId === 'role_user' ? 'user' : bulkRoleId;
      setUsers((prev) => prev.map((u) => (
        selectedUserIds.has(u.user_id) ? { ...u, role: storedRole } : u
      )));
      setSelectedUserIds(new Set());
      setBulkRoleId('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk assign failed');
    } finally {
      setBulkAssigning(false);
    }
  };
  
  const handleAddUser = async () => {
    if (!newUserForm.name || !newUserForm.email || !newUserForm.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setAddingUser(true);
    try {
      await api.post('/admin/users', newUserForm);
      toast.success('User created successfully');
      setShowAddUserDialog(false);
      setNewUserForm({ name: '', email: '', password: '', role: 'user', tier: 'free' });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setAddingUser(false);
    }
  };
  
  const loadTransactions = async () => {
    setTransactionsLoading(true);
    try {
      const response = await api.get('/admin/transactions');
      setTransactions(response.data.transactions || []);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setTransactionsLoading(false);
    }
  };
  
  const loadPlans = async () => {
    setPlansLoading(true);
    try {
      const response = await api.get('/admin/plans');
      setPlans(response.data.plans || []);
    } catch (error) {
      toast.error('Failed to load plans');
    } finally {
      setPlansLoading(false);
    }
  };
  
  // (Plan delete/deactivate migration flow now lives entirely inside
  // AdminPlansTab.jsx — deleted the duplicate handler that never ran.)
  
  const loadSmartPricing = async () => {
    setSmartPricingLoading(true);
    try {
      const response = await api.post('/admin/pricing/calculate', {
        target_profit_margin: targetProfitMargin,
        include_projections: true
      });
      setSmartPricing(response.data);
    } catch (error) {
      toast.error('Failed to calculate pricing suggestions');
    } finally {
      setSmartPricingLoading(false);
    }
  };
  
  const loadSupportData = async () => {
    setSupportLoading(true);
    try {
      const [statsRes, ticketsRes] = await Promise.all([
        api.get('/support/admin/stats'),
        api.get(`/support/admin/tickets?${new URLSearchParams(Object.fromEntries(Object.entries(supportFilter).filter(([_, v]) => v))).toString()}`)
      ]);
      setSupportStats(statsRes.data);
      setSupportTickets(ticketsRes.data.tickets || []);
    } catch (error) {
      toast.error('Failed to load support data');
    } finally {
      setSupportLoading(false);
    }
  };
  
  const handleUpdateTicket = async (ticketId, updates) => {
    setUpdatingTicket(true);
    try {
      await api.patch(`/support/admin/ticket/${ticketId}`, null, { params: updates });
      toast.success('Ticket updated successfully');
      loadSupportData();
      setSelectedTicket(null);
      setTicketResponse('');
    } catch (error) {
      toast.error('Failed to update ticket');
    } finally {
      setUpdatingTicket(false);
    }
  };
  
  // Contact Submissions Functions
  const loadContactSubmissions = async () => {
    setContactLoading(true);
    try {
      const response = await api.get('/contact/submissions');
      setContactSubmissions(response.data || []);
    } catch (error) {
      toast.error('Failed to load contact submissions');
    } finally {
      setContactLoading(false);
    }
  };
  
  const handleContactReply = async () => {
    if (!selectedContact || !contactReply.trim()) {
      toast.error('Please enter a reply message');
      return;
    }
    
    setSendingContactReply(true);
    try {
      await api.post(`/contact/submissions/${selectedContact.submission_id}/reply`, null, {
        params: { reply_message: contactReply }
      });
      toast.success('Reply sent successfully');
      setSelectedContact(null);
      setContactReply('');
      loadContactSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSendingContactReply(false);
    }
  };
  
  const handleUpdateContactStatus = async (submissionId, status) => {
    try {
      await api.patch(`/contact/submissions/${submissionId}`, null, {
        params: { status }
      });
      toast.success('Status updated');
      loadContactSubmissions();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  // Case Studies Functions
  const loadCaseStudies = async () => {
    setCaseStudiesLoading(true);
    try {
      const response = await api.get('/case-studies');
      setCaseStudies(response.data.items || []);
      setCaseStudiesStats(response.data.stats || null);
    } catch (error) {
      toast.error('Failed to load case studies');
    } finally {
      setCaseStudiesLoading(false);
    }
  };
  
  const resetCaseStudyForm = () => {
    setCaseStudyForm({
      title: '',
      summary: '',
      content: '',
      industry: '',
      company_name: '',
      featured_image: '',
      tags: '',
      post_type: 'case_study',
      is_published: false,
      is_featured: false,
      results: []
    });
    setEditingCaseStudy(null);
  };
  
  const openCaseStudyModal = (item = null) => {
    if (item) {
      setEditingCaseStudy(item);
      setCaseStudyForm({
        title: item.title || '',
        summary: item.summary || '',
        content: item.content || '',
        industry: item.industry || '',
        company_name: item.company_name || '',
        featured_image: item.featured_image || '',
        tags: (item.tags || []).join(', '),
        post_type: item.post_type || 'case_study',
        is_published: item.is_published || false,
        is_featured: item.is_featured || false,
        results: item.results || []
      });
    } else {
      resetCaseStudyForm();
    }
    setShowCaseStudyModal(true);
  };
  
  const handleSaveCaseStudy = async () => {
    if (!caseStudyForm.title.trim() || !caseStudyForm.summary.trim()) {
      toast.error('Title and summary are required');
      return;
    }
    
    setSavingCaseStudy(true);
    try {
      const payload = {
        ...caseStudyForm,
        tags: caseStudyForm.tags.split(',').map(t => t.trim()).filter(t => t)
      };
      
      if (editingCaseStudy) {
        await api.patch(`/case-studies/${editingCaseStudy.case_study_id}`, payload);
        toast.success('Case study updated');
      } else {
        await api.post('/case-studies', payload);
        toast.success('Case study created');
      }
      
      setShowCaseStudyModal(false);
      resetCaseStudyForm();
      loadCaseStudies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save case study');
    } finally {
      setSavingCaseStudy(false);
    }
  };
  
  const handleDeleteCaseStudy = async (id) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await api.delete(`/case-studies/${id}`);
      toast.success('Item deleted');
      loadCaseStudies();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };
  
  const handleTogglePublish = async (id) => {
    try {
      const response = await api.post(`/case-studies/${id}/toggle-publish`);
      toast.success(response.data.message);
      loadCaseStudies();
    } catch (error) {
      toast.error('Failed to toggle publish status');
    }
  };
  
  const handleToggleFeatured = async (id) => {
    try {
      const response = await api.post(`/case-studies/${id}/toggle-featured`);
      toast.success(response.data.message);
      loadCaseStudies();
    } catch (error) {
      toast.error('Failed to toggle featured status');
    }
  };
  
  useEffect(() => {
    if (activeTab === 'users') {
      if (users.length === 0) loadUsers();
      if (plans.length === 0) loadPlans(); // Load plans for tier dropdown
      if (roles.length === 0) loadRolesList(); // Load roles for role selector (iter130)
    }
    if (activeTab === 'payments' && transactions.length === 0) loadTransactions();
    if (activeTab === 'plans' && plans.length === 0) loadPlans();
    if (activeTab === 'support' && !supportStats) loadSupportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  
  const handleSaveSmtp = async () => {
    setSaving(true);
    try {
      const response = await api.post('/admin/smtp', smtpConfig);
      setExistingSmtpConfig(response.data);
      toast.success('SMTP configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SMTP configuration');
    } finally {
      setSaving(false);
    }
  };
  
  const handleTestSmtp = async () => {
    if (!testEmail) { toast.error('Please enter a test email'); return; }
    setTestingEmail(true);
    try {
      const response = await api.post('/admin/smtp/test', { email: testEmail });
      if (response.data.success) toast.success(`Test email sent to ${testEmail}`);
      else toast.error(response.data.message || 'Failed to send test email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };
  
  const handleSavePaddle = async () => {
    setSaving(true);
    try {
      const response = await api.post('/admin/paddle', paddleConfig);
      setExistingPaddleConfig(response.data);
      toast.success('Paddle configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Paddle configuration');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveResellerSettings = async () => {
    setSaving(true);
    try {
      await api.post('/admin/reseller-settings', resellerSettings);
      toast.success('Reseller settings saved');
    } catch (error) {
      toast.error('Failed to save reseller settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleGhostLogin = async (u) => {
    const confirmed = window.confirm(
      `Ghost-login as ${u.name || u.email}?\n\n` +
      `You'll be signed in as this user for 30 minutes. Every action you take will be attributed to them and logged in the audit trail against your admin email. Click OK to proceed.`
    );
    if (!confirmed) return;
    try {
      const res = await api.post(`/admin/users/${u.user_id}/impersonate`);
      // Stash the admin session so "Exit ghost mode" can restore it.
      const currentAdmin = localStorage.getItem('harmonix_st');
      if (currentAdmin) sessionStorage.setItem('harmonix_ghost_return', currentAdmin);
      // Replace the active session with the ghost token.
      localStorage.setItem('harmonix_st', res.data.session_token);
      sessionStorage.setItem('harmonix_ghost_meta', JSON.stringify({
        target_email: res.data.user?.email,
        impersonated_by_email: res.data.impersonated_by_email,
        expires_at: res.data.expires_at,
      }));
      toast.success(`Now viewing Harmonix as ${res.data.user?.email}`);
      // Full reload so every context (AuthContext, RBAC guards, etc.) re-reads /auth/me under the new session.
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Ghost login failed');
    }
  };

  const handleUserAction = async (userId, action) => {
    try {
      await api.post(`/admin/users/${userId}/${action}`);
      toast.success(`User ${action} successful`);
      loadUsers();
    } catch (error) {
      toast.error(`Failed to ${action} user`);
    }
  };
  
  const handleChangeUserSubscription = async () => {
    if (!editingUserSub || !newUserTier) return;
    setSavingSubscription(true);
    try {
      await api.patch(`/admin/users/${editingUserSub.user_id}/subscription`, {
        tier: newUserTier,
        billing_cycle: 'monthly'
      });
      toast.success(`Subscription updated to ${newUserTier}`);
      setEditingUserSub(null);
      setNewUserTier('');
      loadUsers();
    } catch (error) {
      toast.error('Failed to update subscription');
    } finally {
      setSavingSubscription(false);
    }
  };
  
  const togglePassword = (field) => setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  
  if (!isSystemAdmin && !canSeeAnyAdminTab) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="bg-white border-red-200 shadow-sm max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-600">You don&apos;t have permission to access the admin panel.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Platform Administration</h1>
          <p className="text-slate-600 mt-1">Manage your SaaS platform, users, and integrations</p>
        </div>
        <div className="flex items-center gap-3">
          <HelpButton 
            title="Admin Dashboard"
            content={`
              <p>Welcome to the Platform Administration panel. As a System Admin, you have full control over:</p>
              <ul>
                <li><strong>Overview:</strong> System health, revenue metrics, and key stats</li>
                <li><strong>Users:</strong> View, search, suspend, delete users, and change subscription tiers</li>
                <li><strong>Plans:</strong> Create, edit, and manage subscription plans with user migration support</li>
                <li><strong>Payments:</strong> Track all transactions and revenue</li>
                <li><strong>Resellers:</strong> Configure commission rates and payout settings</li>
                <li><strong>SMTP:</strong> Configure email delivery</li>
                <li><strong>Stripe:</strong> Configure payment processing</li>
              </ul>
              <p class="mt-2"><strong>Plan Migration:</strong> When deleting or deactivating a plan with active subscribers, you'll be prompted to migrate users to another plan first.</p>
            `}
          />
          <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50">
            <Shield className="w-3 h-3 mr-1" />
            System Admin
          </Badge>
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100 border border-slate-200 flex flex-wrap h-auto gap-1 p-1">
          {canSee('overview') && <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-overview"><Activity className="w-4 h-4 mr-2" />Overview</TabsTrigger>}
          {canSee('users') && <TabsTrigger value="users" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-2" />Users</TabsTrigger>}
          {canSee('plans') && <TabsTrigger value="plans" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-plans"><Package className="w-4 h-4 mr-2" />Plans</TabsTrigger>}
          {canSee('payments') && <TabsTrigger value="payments" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-payments"><DollarSign className="w-4 h-4 mr-2" />Payments</TabsTrigger>}
          {canSee('resellers') && <TabsTrigger value="resellers" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-resellers"><Percent className="w-4 h-4 mr-2" />Resellers</TabsTrigger>}
          {canSee('content') && <TabsTrigger value="content" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-content" onClick={() => !caseStudiesStats && loadCaseStudies()}><Newspaper className="w-4 h-4 mr-2" />Content</TabsTrigger>}
          {canSee('docs') && <TabsTrigger value="docs" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-docs"><Book className="w-4 h-4 mr-2" />Docs</TabsTrigger>}
          {canSee('support') && <TabsTrigger value="support" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-support"><MessageSquare className="w-4 h-4 mr-2" />Support</TabsTrigger>}
          {canSee('smtp') && <TabsTrigger value="smtp" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-smtp"><Mail className="w-4 h-4 mr-2" />SMTP</TabsTrigger>}
          {canSee('stripe') && <TabsTrigger value="stripe" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-stripe"><CreditCard className="w-4 h-4 mr-2" />Stripe</TabsTrigger>}
          {canSee('social') && <TabsTrigger value="social" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-social"><Share2 className="w-4 h-4 mr-2" />Social</TabsTrigger>}
          {canSee('pricing_config') && <TabsTrigger value="integrations" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-integrations"><Plug className="w-4 h-4 mr-2" />Integrations</TabsTrigger>}
          {canSee('roles') && <TabsTrigger value="roles" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-roles"><KeyRound className="w-4 h-4 mr-2" />Roles</TabsTrigger>}
          {isSystemAdmin && <TabsTrigger value="seo_ab" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-seo-ab"><Activity className="w-4 h-4 mr-2" />SEO A/B</TabsTrigger>}
          {isSystemAdmin && <TabsTrigger value="reports" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-reports"><BarChart3 className="w-4 h-4 mr-2" />Reports</TabsTrigger>}
          {isSystemAdmin && <TabsTrigger value="system_health" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="admin-tab-system-health"><Activity className="w-4 h-4 mr-2" />Health</TabsTrigger>}
        </TabsList>
        
        {/* OVERVIEW TAB */}
        <TabsContent value="overview">
          <div className="space-y-6">
            <AdminOverviewTab 
              basicStats={stats}
              revenueStats={revenueStats}
              smtpConfigured={!!existingSmtpConfig}
              onRefresh={loadData}
            />
            {/* iter128.1 — replacement for the removed auto-cross-tenant leak */}
            <AdminAgentSearch />
          </div>
        </TabsContent>
        
        {/* USERS TAB */}
        <TabsContent value="users">
          <AdminUsersTab
            users={users}
            usersLoading={usersLoading}
            userSearch={userSearch}
            setUserSearch={setUserSearch}
            loadUsers={loadUsers}
            handleUserAction={handleUserAction}
            handleGhostLogin={handleGhostLogin}
            onEditPermissions={openPermsOverride}
            editingUserSub={editingUserSub}
            setEditingUserSub={setEditingUserSub}
            newUserTier={newUserTier}
            setNewUserTier={setNewUserTier}
            handleChangeUserSubscription={handleChangeUserSubscription}
            savingSubscription={savingSubscription}
            showAddUserDialog={showAddUserDialog}
            setShowAddUserDialog={setShowAddUserDialog}
            newUserForm={newUserForm}
            setNewUserForm={setNewUserForm}
            handleAddUser={handleAddUser}
            addingUser={addingUser}
            plans={plans}
            roles={roles}
            handleAssignRole={handleAssignRole}
          />

          {/* Per-User Permission Overrides Dialog (restored iter124 — was orphaned during earlier refactor) */}
          <Dialog open={!!editingPermsUser} onOpenChange={(open) => { if (!open) { setEditingPermsUser(null); setOverrideForm({}); } }}>
            <DialogContent className="bg-white max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="perms-override-dialog">
              <DialogHeader>
                <DialogTitle className="text-slate-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  Permission Overrides — {editingPermsUser?.name}
                </DialogTitle>
              </DialogHeader>
              {editingPermsUser && (
                <div className="space-y-4">
                  <div className="p-3 bg-slate-50 rounded-lg text-sm">
                    <p className="text-slate-700">
                      User&apos;s current role: <span className="font-medium">
                        {(() => {
                          const r = rbacRoles.find((x) => x.role_id === editingPermsUser._role)
                            || rbacRoles.find((x) => x.role_id === 'role_system_admin' && editingPermsUser._role === 'system_admin');
                          return r?.name || editingPermsUser._role || 'user';
                        })()}
                      </span>
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Toggles below override the role&apos;s defaults for this specific user. Leave a row untouched to inherit from the role.
                    </p>
                  </div>

                  {(editingPermsUser._role === 'system_admin' || editingPermsUser._role === 'role_system_admin') && (
                    <div className="p-3 rounded-lg border-2 border-amber-200 bg-amber-50 text-sm text-amber-900" data-testid="perms-sysadmin-warning">
                      System Admin permissions cannot be overridden — this role always has full access by design.
                    </div>
                  )}

                  <PermissionMatrix
                    segments={rbacSegments}
                    actions={rbacActions}
                    value={overrideForm}
                    onChange={setOverrideForm}
                  />

                  <div className="flex justify-between items-center pt-2">
                    <Button
                      variant="ghost" onClick={clearOverrides} disabled={savingOverrides}
                      className="text-red-600 hover:bg-red-50"
                      data-testid="clear-overrides-btn"
                    >
                      Clear all overrides
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => { setEditingPermsUser(null); setOverrideForm({}); }}
                        className="border-slate-300"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={saveOverrides} disabled={savingOverrides}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="save-overrides-btn"
                      >
                        {savingOverrides ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Save Overrides
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>
        
        {/* PLANS TAB */}
        <TabsContent value="plans">
          <AdminPlansTab
            plans={plans}
            loadPlans={loadPlans}
            showPlanModal={showPlanModal}
            setShowPlanModal={setShowPlanModal}
            editingPlan={editingPlan}
            setEditingPlan={setEditingPlan}
            suggestedPlanData={suggestedPlanData}
            setSuggestedPlanData={setSuggestedPlanData}
            targetProfitMargin={targetProfitMargin}
            setTargetProfitMargin={setTargetProfitMargin}
            smartPricingLoading={smartPricingLoading}
            setSmartPricingLoading={setSmartPricingLoading}
            smartPricingData={smartPricingData}
            setSmartPricingData={setSmartPricingData}
            PlanModal={PlanModal}
          />
        </TabsContent>
        
        {/* PAYMENTS TAB */}
        <TabsContent value="payments">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-slate-900 flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-600" />Payment Transactions</CardTitle>
                <CardDescription className="text-slate-600">Track all payments received and disbursed</CardDescription>
              </div>
              <HelpButton title="Payment Tracking" content={`<p>View all financial transactions:</p><ul><li><strong>Received:</strong> Payments from subscribers</li><li><strong>Disbursed:</strong> Payouts to resellers</li><li><strong>Status:</strong> Successful, pending, failed, refunded</li></ul>`} />
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">Total Received</p>
                  <p className="text-2xl font-bold text-slate-900">${(revenueStats?.total_received || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-700">Total Disbursed</p>
                  <p className="text-2xl font-bold text-slate-900">${(revenueStats?.total_disbursed || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700">Pending</p>
                  <p className="text-2xl font-bold text-slate-900">${(revenueStats?.pending || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">Refunded</p>
                  <p className="text-2xl font-bold text-slate-900">${(revenueStats?.refunded || 0).toLocaleString()}</p>
                </div>
              </div>
              
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 text-sm text-slate-600">Date</th>
                      <th className="text-left p-3 text-sm text-slate-600">User</th>
                      <th className="text-left p-3 text-sm text-slate-600">Type</th>
                      <th className="text-left p-3 text-sm text-slate-600">Amount</th>
                      <th className="text-left p-3 text-sm text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length === 0 ? (
                      <tr><td colSpan={5} className="p-8 text-center text-slate-500">No transactions yet</td></tr>
                    ) : transactions.map((t, i) => (
                      <tr key={t.transaction_id || t.id || `tx-${i}-${t.created_at}-${t.user_email}`} className="border-t border-slate-200">
                        <td className="p-3 text-slate-700">{new Date(t.created_at).toLocaleDateString()}</td>
                        <td className="p-3 text-slate-900">{t.user_email}</td>
                        <td className="p-3"><Badge variant="outline" className={t.type === 'received' ? 'border-green-300 text-green-700 bg-green-50' : 'border-purple-300 text-purple-700 bg-purple-50'}>{t.type}</Badge></td>
                        <td className="p-3 text-slate-900 font-medium">${t.amount.toFixed(2)}</td>
                        <td className="p-3"><Badge className={t.status === 'successful' ? 'bg-green-100 text-green-700' : t.status === 'pending' ? 'bg-amber-100 text-amber-700' : t.status === 'refunded' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}>{t.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* RESELLERS TAB */}
        <TabsContent value="resellers">
          <AdminTabBoundary tabName="Resellers">
            <AdminResellersTab />
          </AdminTabBoundary>
        </TabsContent>
        
        {/* CONTENT (CASE STUDIES / BLOGS) TAB */}
        <TabsContent value="content">
          <div className="space-y-6">
            {/* Stats Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard label="Total Items" value={caseStudiesStats?.total || 0} icon={Newspaper} color="blue" />
              <MetricCard label="Published" value={caseStudiesStats?.published || 0} icon={Globe} color="emerald" />
              <MetricCard label="Case Studies" value={caseStudiesStats?.case_studies || 0} icon={FileText} color="purple" />
              <MetricCard label="Blog Posts" value={caseStudiesStats?.blogs || 0} icon={Book} color="amber" />
            </div>
            
            {/* Content Management */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Newspaper className="w-5 h-5 text-blue-600" />
                    Case Studies & Blog Posts
                  </CardTitle>
                  <CardDescription className="text-slate-600">
                    Manage case studies and blog content for marketing
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={loadCaseStudies} disabled={caseStudiesLoading} className="border-slate-300 text-slate-700">
                    {caseStudiesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                  <Button onClick={() => openCaseStudyModal()} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add New
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {caseStudiesLoading && caseStudies.length === 0 ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                  </div>
                ) : caseStudies.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-slate-300 rounded-lg">
                    <Newspaper className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-600 mb-2">No content yet</p>
                    <p className="text-sm text-slate-500 mb-4">Create case studies and blog posts to showcase on your site</p>
                    <Button onClick={() => openCaseStudyModal()} className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Post
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {caseStudies.map((item) => (
                      <div key={item.case_study_id} className="p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-slate-900 truncate">{item.title}</h4>
                              {item.is_featured && (
                                <Star className="w-4 h-4 text-amber-500 fill-amber-500 flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-sm text-slate-500 line-clamp-2 mb-2">{item.summary}</p>
                            <div className="flex flex-wrap items-center gap-2 text-xs">
                              <Badge variant="outline" className={item.post_type === 'case_study' ? 'border-purple-300 text-purple-700 bg-purple-50' : 'border-blue-300 text-blue-700 bg-blue-50'}>
                                {item.post_type === 'case_study' ? 'Case Study' : 'Blog Post'}
                              </Badge>
                              {item.industry && (
                                <Badge variant="outline" className="border-slate-300 text-slate-600">{item.industry}</Badge>
                              )}
                              <span className="text-slate-400">
                                {item.view_count || 0} views
                              </span>
                              <span className="text-slate-400">
                                {new Date(item.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge className={item.is_published ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                              {item.is_published ? 'Published' : 'Draft'}
                            </Badge>
                            <Button variant="ghost" size="icon" onClick={() => handleToggleFeatured(item.case_study_id)} className="text-slate-500 hover:text-amber-600" title={item.is_featured ? 'Unfeature' : 'Feature'}>
                              <Star className={`w-4 h-4 ${item.is_featured ? 'fill-amber-500 text-amber-500' : ''}`} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleTogglePublish(item.case_study_id)} className="text-slate-500 hover:text-blue-600" title={item.is_published ? 'Unpublish' : 'Publish'}>
                              <Globe className={`w-4 h-4 ${item.is_published ? 'text-emerald-600' : ''}`} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openCaseStudyModal(item)} className="text-slate-500 hover:text-slate-900" title="Edit">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteCaseStudy(item.case_study_id)} className="text-slate-500 hover:text-red-600" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Case Study / Blog Modal */}
          <Dialog open={showCaseStudyModal} onOpenChange={(open) => { setShowCaseStudyModal(open); if (!open) resetCaseStudyForm(); }}>
            <DialogContent className="max-w-3xl bg-white border-slate-200 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-slate-900">
                  {editingCaseStudy ? 'Edit Content' : 'Create New Content'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                {/* Type Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-600">Content Type</Label>
                    <Select value={caseStudyForm.post_type} onValueChange={(v) => setCaseStudyForm({ ...caseStudyForm, post_type: v })}>
                      <SelectTrigger className="bg-white border-slate-300 text-slate-900 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="case_study">Case Study</SelectItem>
                        <SelectItem value="blog">Blog Post</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-600">Industry</Label>
                    <Input
                      value={caseStudyForm.industry}
                      onChange={(e) => setCaseStudyForm({ ...caseStudyForm, industry: e.target.value })}
                      placeholder="e.g., Healthcare, E-commerce"
                      className="bg-white border-slate-300 text-slate-900 mt-1"
                    />
                  </div>
                </div>
                
                {/* Title */}
                <div>
                  <Label className="text-slate-600">Title *</Label>
                  <Input
                    value={caseStudyForm.title}
                    onChange={(e) => setCaseStudyForm({ ...caseStudyForm, title: e.target.value })}
                    placeholder="Enter a compelling title"
                    className="bg-white border-slate-300 text-slate-900 mt-1"
                  />
                </div>
                
                {/* Summary */}
                <div>
                  <Label className="text-slate-600">Summary *</Label>
                  <Textarea
                    value={caseStudyForm.summary}
                    onChange={(e) => setCaseStudyForm({ ...caseStudyForm, summary: e.target.value })}
                    placeholder="Brief summary for listings and previews"
                    className="bg-white border-slate-300 text-slate-900 mt-1 resize-none"
                    rows={2}
                  />
                </div>
                
                {/* Content */}
                <div>
                  <Label className="text-slate-600">Content (Markdown supported)</Label>
                  <Textarea
                    value={caseStudyForm.content}
                    onChange={(e) => setCaseStudyForm({ ...caseStudyForm, content: e.target.value })}
                    placeholder="Write your full content here. Supports Markdown formatting."
                    className="bg-white border-slate-300 text-slate-900 mt-1 resize-none font-mono text-sm"
                    rows={10}
                  />
                </div>
                
                {/* Company Name (for case studies) */}
                {caseStudyForm.post_type === 'case_study' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600">Company Name</Label>
                      <Input
                        value={caseStudyForm.company_name}
                        onChange={(e) => setCaseStudyForm({ ...caseStudyForm, company_name: e.target.value })}
                        placeholder="Featured company name"
                        className="bg-white border-slate-300 text-slate-900 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-600">Featured Image URL</Label>
                      <Input
                        value={caseStudyForm.featured_image}
                        onChange={(e) => setCaseStudyForm({ ...caseStudyForm, featured_image: e.target.value })}
                        placeholder="https://..."
                        className="bg-white border-slate-300 text-slate-900 mt-1"
                      />
                    </div>
                  </div>
                )}
                
                {/* Tags */}
                <div>
                  <Label className="text-slate-600">Tags (comma-separated)</Label>
                  <Input
                    value={caseStudyForm.tags}
                    onChange={(e) => setCaseStudyForm({ ...caseStudyForm, tags: e.target.value })}
                    placeholder="AI, Customer Support, Automation"
                    className="bg-white border-slate-300 text-slate-900 mt-1"
                  />
                </div>
                
                {/* Publish/Featured Options */}
                <div className="flex items-center gap-6 pt-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={caseStudyForm.is_published}
                      onCheckedChange={(v) => setCaseStudyForm({ ...caseStudyForm, is_published: v })}
                      className="data-[state=checked]:bg-emerald-600"
                    />
                    <Label className="text-slate-600">Publish immediately</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={caseStudyForm.is_featured}
                      onCheckedChange={(v) => setCaseStudyForm({ ...caseStudyForm, is_featured: v })}
                      className="data-[state=checked]:bg-amber-600"
                    />
                    <Label className="text-slate-600">Feature on homepage</Label>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                  <Button variant="outline" onClick={() => { setShowCaseStudyModal(false); resetCaseStudyForm(); }} className="border-slate-300 text-slate-700">
                    Cancel
                  </Button>
                  <Button onClick={handleSaveCaseStudy} disabled={savingCaseStudy} className="bg-blue-600 hover:bg-blue-700">
                    {savingCaseStudy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {editingCaseStudy ? 'Update' : 'Create'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>
        
        {/* SMTP TAB */}
        <TabsContent value="smtp">
          <AdminTabBoundary tabName="SMTP Configuration">
            <AdminSmtpTab />
          </AdminTabBoundary>
        </TabsContent>
        
        {/* STRIPE TAB */}
        <TabsContent value="stripe">
          <AdminTabBoundary tabName="Stripe Configuration">
            <AdminStripeTab />
          </AdminTabBoundary>
        </TabsContent>
        
        {/* SOCIAL MEDIA INTEGRATION TAB */}
        <TabsContent value="social">
          <AdminTabBoundary tabName="Social Media Integration">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-slate-900 flex items-center gap-2"><Share2 className="w-5 h-5 text-blue-600" />Social Media Integration</CardTitle>
                <CardDescription className="text-slate-600">Configure Meta Business Suite APIs for WhatsApp, Instagram, and Facebook</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {socialConfigured?.secret_health === 'broken' && (
                <div className="p-4 bg-red-50 border border-red-300 rounded-lg" data-testid="meta-secret-broken-banner">
                  <p className="text-sm font-semibold text-red-900">
                    ⚠️ Stored Meta credentials cannot be decrypted in this environment.
                  </p>
                  <p className="text-xs text-red-800 mt-1">
                    This usually happens when credentials were saved in a different environment (e.g. preview) and the encryption key on this server is different. Facebook/Instagram OAuth will fail with a 500 error until you <strong>re-enter and Save the Meta App Secret &amp; Access Token below</strong>. Your Meta App ID is unaffected.
                  </p>
                  {socialConfigured.secret_health_detail && (
                    <details className="mt-2 text-xs text-red-700">
                      <summary className="cursor-pointer">Technical detail</summary>
                      <code className="block mt-1 break-words">{socialConfigured.secret_health_detail}</code>
                    </details>
                  )}
                </div>
              )}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Setup Instructions</h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Go to <a href="https://business.facebook.com/" target="_blank" rel="noopener noreferrer" className="underline">business.facebook.com</a> and create a Business Account</li>
                  <li>Create an App at <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer" className="underline">developers.facebook.com</a></li>
                  <li>Add Messenger, WhatsApp, and Instagram products to your app</li>
                  <li>Copy the credentials below</li>
                </ol>
                <a href="/memory/SOCIAL_MEDIA_INTEGRATION_GUIDE.md" target="_blank" className="text-sm text-blue-700 underline mt-2 inline-block">
                  View Full Integration Guide →
                </a>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">Meta App ID</Label>
                  <Input
                    value={socialConfig.meta_app_id}
                    onChange={(e) => setSocialConfig({ ...socialConfig, meta_app_id: e.target.value })}
                    placeholder="Your Meta App ID"
                    className="bg-white border-slate-300"
                    data-testid="meta-app-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-600">Meta App Secret</Label>
                    {socialConfigured.meta_app_secret_configured && (
                      <span className="text-xs text-emerald-600 font-medium" data-testid="meta-app-secret-saved-badge">
                        ✓ Saved — leave blank to keep
                      </span>
                    )}
                  </div>
                  <Input
                    type="password"
                    value={socialConfig.meta_app_secret}
                    onChange={(e) => setSocialConfig({ ...socialConfig, meta_app_secret: e.target.value })}
                    placeholder={socialConfigured.meta_app_secret_configured ? '••••••••••• (already saved)' : 'Your Meta App Secret'}
                    className="bg-white border-slate-300"
                    data-testid="meta-app-secret-input"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-600">Meta Access Token</Label>
                    {socialConfigured.meta_access_token_configured && (
                      <span className="text-xs text-emerald-600 font-medium" data-testid="meta-access-token-saved-badge">
                        ✓ Saved — leave blank to keep
                      </span>
                    )}
                  </div>
                  <Input
                    type="password"
                    value={socialConfig.meta_access_token}
                    onChange={(e) => setSocialConfig({ ...socialConfig, meta_access_token: e.target.value })}
                    placeholder={socialConfigured.meta_access_token_configured ? '••••••••••• (already saved)' : 'System User Access Token'}
                    className="bg-white border-slate-300"
                    data-testid="meta-access-token-input"
                  />
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-900 mb-3">WhatsApp Business</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Phone Number ID</Label>
                    <Input
                      value={socialConfig.whatsapp_phone_id}
                      onChange={(e) => setSocialConfig({ ...socialConfig, whatsapp_phone_id: e.target.value })}
                      placeholder="WhatsApp Phone Number ID"
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Business Account ID</Label>
                    <Input
                      value={socialConfig.whatsapp_business_id}
                      onChange={(e) => setSocialConfig({ ...socialConfig, whatsapp_business_id: e.target.value })}
                      placeholder="WhatsApp Business Account ID"
                      className="bg-white border-slate-300"
                    />
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-900 mb-3">Instagram & Facebook</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Instagram Business Account ID</Label>
                    <Input
                      value={socialConfig.instagram_account_id}
                      onChange={(e) => setSocialConfig({ ...socialConfig, instagram_account_id: e.target.value })}
                      placeholder="Instagram Business Account ID"
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Facebook Page ID</Label>
                    <Input
                      value={socialConfig.facebook_page_id}
                      onChange={(e) => setSocialConfig({ ...socialConfig, facebook_page_id: e.target.value })}
                      placeholder="Facebook Page ID"
                      className="bg-white border-slate-300"
                    />
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-900 mb-3">Webhook Configuration</h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Webhook Verify Token</Label>
                    <Input
                      value={socialConfig.webhook_verify_token}
                      onChange={(e) => setSocialConfig({ ...socialConfig, webhook_verify_token: e.target.value })}
                      placeholder="Create a secure token (any string)"
                      className="bg-white border-slate-300"
                    />
                    <p className="text-xs text-slate-500">Use this token when configuring webhooks in Meta Developer Console</p>
                  </div>
                  <div className="p-3 bg-slate-100 rounded-lg">
                    <Label className="text-slate-600 text-sm">Your Webhook URL</Label>
                    <code className="block mt-1 p-2 bg-white border rounded text-sm text-slate-700 break-all">
                      {window.location.origin}/api/webhooks/meta
                    </code>
                  </div>
                </div>
              </div>
              
              {/* Google Calendar Integration Guide */}
              <div className="pt-6 mt-6 border-t border-slate-200">
                <div className="flex items-start gap-3 mb-4">
                  <Calendar className="w-6 h-6 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Google Calendar Integration</h3>
                    <p className="text-sm text-slate-600">Enable appointment scheduling with Google Calendar</p>
                  </div>
                </div>
                
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
                  <div>
                    <h4 className="font-medium text-blue-900 mb-2">Setup Instructions</h4>
                    <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                      <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline font-medium">Google Cloud Console</a></li>
                      <li>Create a new project or select an existing one</li>
                      <li>Enable the "Google Calendar API" from the API Library</li>
                      <li>Go to "Credentials" and create an "OAuth 2.0 Client ID"</li>
                      <li>Configure the OAuth consent screen if prompted</li>
                      <li>Set the redirect URI to: <code className="bg-blue-100 px-1 rounded">{window.location.origin}/api/auth/google/callback</code></li>
                      <li>Copy the Client ID and Client Secret</li>
                    </ol>
                  </div>
                  
                  <div className="p-3 bg-white border border-blue-200 rounded-lg">
                    <h5 className="text-sm font-medium text-slate-700 mb-2">Required Environment Variables</h5>
                    <div className="font-mono text-xs bg-slate-900 text-green-400 p-3 rounded overflow-x-auto">
                      <div>GOOGLE_CLIENT_ID=your_client_id_here</div>
                      <div>GOOGLE_CLIENT_SECRET=your_client_secret_here</div>
                      <div>GOOGLE_CALENDAR_REDIRECT_URI={window.location.origin}/api/calendar/callback</div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-blue-700">
                    <AlertTriangle className="w-3 h-3 inline-block mr-1" />
                    These variables must be set in your deployment environment. Contact your system administrator for production deployments.
                  </p>
                </div>
              </div>
              
              <Button 
                onClick={async () => {
                  setSavingSocialConfig(true);
                  try {
                    await api.post('/admin/config/social', socialConfig);
                    toast.success('Social media configuration saved');
                    // Refresh the masked "configured" indicators
                    try {
                      const refreshed = await api.get('/admin/config/social');
                      if (refreshed.data?.configured) {
                        setSocialConfigured({
                          meta_app_secret_configured: !!refreshed.data.meta_app_secret_configured,
                          meta_access_token_configured: !!refreshed.data.meta_access_token_configured,
                        });
                      }
                    } catch (e) { /* ignore */ }
                    // Clear the secret input boxes (we never display the real secret again)
                    setSocialConfig((prev) => ({ ...prev, meta_app_secret: '', meta_access_token: '' }));
                  } catch (error) {
                    const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
                    const status = error?.response?.status;
                    toast.error(`Save failed${status ? ` (HTTP ${status})` : ''}: ${detail}`);
                  } finally {
                    setSavingSocialConfig(false);
                  }
                }} 
                disabled={savingSocialConfig} 
                className="bg-blue-600 hover:bg-blue-700 mt-6"
                data-testid="save-social-config-btn"
              >
                {savingSocialConfig ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Social Media Configuration
              </Button>
            </CardContent>
          </Card>
          </AdminTabBoundary>
        </TabsContent>
        
        {/* ROLES & PERMISSIONS TAB */}
        <TabsContent value="roles">
          <AdminTabBoundary tabName="Roles & Permissions">
            <AdminRolesTab />
          </AdminTabBoundary>
        </TabsContent>

        {/* SEO A/B TAB */}
        <TabsContent value="seo_ab">
          <AdminTabBoundary tabName="SEO A/B Experiments">
            <AdminSeoAbTab />
          </AdminTabBoundary>
        </TabsContent>

        {/* SYSTEM HEALTH TAB */}
        <TabsContent value="system_health">
          <AdminTabBoundary tabName="System Health">
            <AdminSystemHealthTab />
          </AdminTabBoundary>
        </TabsContent>

        {/* REPORTS TAB (iter126 — Retention Digest) */}
        <TabsContent value="reports">
          <AdminTabBoundary tabName="Reports">
            <AdminReportsTab />
          </AdminTabBoundary>
        </TabsContent>

        {/* INTEGRATIONS TAB (Service Costs + LLM Providers + API Keys) */}
        <TabsContent value="integrations">
          <AdminTabBoundary tabName="Integrations">
            <AdminIntegrationsTab />
          </AdminTabBoundary>
        </TabsContent>
        
        {/* DOCUMENTATION TAB */}
        <TabsContent value="docs">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-slate-900 flex items-center gap-2"><Book className="w-5 h-5 text-blue-600" />User Manual Management</CardTitle>
                <CardDescription className="text-slate-600">Upload and manage the user manual document</CardDescription>
              </div>
              <HelpButton title="User Manual" content={`<p>Manage the downloadable user manual for your platform users.</p><ul><li><strong>Upload:</strong> Upload a PDF or Word document</li><li><strong>Version:</strong> Track document versions</li><li><strong>Fallback:</strong> If no document uploaded, users see the web version</li></ul><p class="mt-2">The manual should include platform features and how-to guides with screenshots.</p>`} />
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Status */}
              <div className={`p-4 rounded-lg border ${manualConfig?.has_uploaded_document ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-3">
                  {manualConfig?.has_uploaded_document ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  )}
                  <div>
                    <p className={manualConfig?.has_uploaded_document ? 'text-green-700 font-medium' : 'text-amber-700 font-medium'}>
                      {manualConfig?.has_uploaded_document ? 'Document Uploaded' : 'No Document Uploaded'}
                    </p>
                    <p className="text-sm text-slate-500">
                      {manualConfig?.has_uploaded_document 
                        ? `${manualConfig.uploaded_file_name} • v${manualConfig.version}`
                        : 'Users will be directed to the online user manual page'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Upload Section */}
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-700 mb-2 block">Upload User Manual</Label>
                  <div className="flex items-center gap-3">
                    <label className="flex-1">
                      <div className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                        <Upload className="w-5 h-5 text-slate-400" />
                        <span className="text-slate-600">Click to upload PDF or Word document</span>
                      </div>
                      <input 
                        type="file" 
                        accept=".pdf,.doc,.docx" 
                        className="hidden" 
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          setUploadingManual(true);
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          try {
                            await api.post('/admin/user-manual/upload', formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            toast.success('User manual uploaded successfully');
                            loadData();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to upload');
                          } finally {
                            setUploadingManual(false);
                            e.target.value = '';
                          }
                        }}
                        disabled={uploadingManual}
                      />
                    </label>
                    {uploadingManual && <Loader2 className="w-5 h-5 animate-spin text-blue-600" />}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Supported formats: PDF, DOC, DOCX. Max size: 50MB</p>
                </div>
                
                {/* Version Control */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700">Version Number</Label>
                    <div className="flex items-center gap-2">
                      <Input 
                        value={manualVersion} 
                        onChange={(e) => setManualVersion(e.target.value)} 
                        placeholder="1.0.0"
                        className="bg-white border-slate-300 text-slate-900"
                      />
                      <Button 
                        variant="outline" 
                        onClick={async () => {
                          try {
                            await api.patch('/admin/user-manual/version', { version: manualVersion });
                            toast.success('Version updated');
                            loadData();
                          } catch (error) {
                            toast.error('Failed to update version');
                          }
                        }}
                        className="border-slate-300 text-slate-700"
                      >
                        Update
                      </Button>
                    </div>
                  </div>
                  
                  {manualConfig?.has_uploaded_document && (
                    <div className="space-y-2">
                      <Label className="text-slate-700">Actions</Label>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          onClick={() => window.open('/api/docs/user-manual/download', '_blank')}
                          className="border-slate-300 text-slate-700"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                        <Button 
                          variant="outline" 
                          onClick={async () => {
                            if (confirm('Are you sure you want to delete the uploaded manual?')) {
                              try {
                                await api.delete('/admin/user-manual');
                                toast.success('Manual deleted');
                                loadData();
                              } catch (error) {
                                toast.error('Failed to delete');
                              }
                            }
                          }}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-blue-700 font-medium mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  User Manual Content Guidelines
                </h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Include platform features overview with screenshots</li>
                  <li>• Step-by-step guides for common tasks (creating AI agents, uploading documents)</li>
                  <li>• Knowledge base management instructions</li>
                  <li>• Rules configuration and calendar booking setup</li>
                  <li>• Billing and subscription management</li>
                  <li>• Troubleshooting and FAQ section</li>
                </ul>
              </div>
              
              {/* File Info */}
              {manualConfig?.has_uploaded_document && (
                <div className="text-sm text-slate-500">
                  <p>File: {manualConfig.uploaded_file_name}</p>
                  <p>Size: {(manualConfig.uploaded_file_size / 1024 / 1024).toFixed(2)} MB</p>
                  <p>Uploaded: {new Date(manualConfig.uploaded_at).toLocaleString()}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* SUPPORT & FEEDBACK TAB */}
        <TabsContent value="support">
          <AdminSupportTab
            supportStats={supportStats}
            supportTickets={supportTickets}
            supportFilter={supportFilter}
            supportLoading={supportLoading}
            selectedTicket={selectedTicket}
            ticketResponse={ticketResponse}
            updatingTicket={updatingTicket}
            setSupportFilter={setSupportFilter}
            setSelectedTicket={setSelectedTicket}
            setTicketResponse={setTicketResponse}
            loadSupportData={loadSupportData}
            handleUpdateTicket={handleUpdateTicket}
            contactSubmissions={contactSubmissions}
            contactLoading={contactLoading}
            selectedContact={selectedContact}
            contactReply={contactReply}
            sendingContactReply={sendingContactReply}
            setSelectedContact={setSelectedContact}
            setContactReply={setContactReply}
            loadContactSubmissions={loadContactSubmissions}
            handleUpdateContactStatus={handleUpdateContactStatus}
            handleContactReply={handleContactReply}
          />
        </TabsContent>
      </Tabs>
      
      {/* iter131 — Role Change Warning for users with active moderator seats */}
      <RoleChangeWarningModal
        prompt={roleChangePrompt}
        onCancel={() => setRoleChangePrompt({ open: false, userId: null, roleId: null, seats: [], userLabel: '' })}
        onConfirm={async () => {
          const { userId, roleId } = roleChangePrompt;
          setRoleChangePrompt({ open: false, userId: null, roleId: null, seats: [], userLabel: '' });
          await _performRoleChange(userId, roleId);
        }}
      />
    </div>
  );
};

// Sub-components
const MetricCard = ({ label, value, icon: Icon, color, trend }) => {
  const colors = { blue: 'from-blue-100 border-blue-200', emerald: 'from-emerald-100 border-emerald-200', green: 'from-green-100 border-green-200', amber: 'from-amber-100 border-amber-200', purple: 'from-purple-100 border-purple-200' };
  const iconColors = { blue: 'text-blue-600', emerald: 'text-emerald-600', green: 'text-green-600', amber: 'text-amber-600', purple: 'text-purple-600' };
  return (
    <Card className={`bg-gradient-to-br ${colors[color]} to-white border shadow-sm`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div><p className="text-sm text-slate-600">{label}</p><p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>{trend && <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" />{trend}</p>}</div>
          <Icon className={`w-8 h-8 ${iconColors[color]} opacity-70`} />
        </div>
      </CardContent>
    </Card>
  );
};

const HealthIndicator = ({ name, status, details }) => {
  const cfg = { healthy: { color: 'bg-green-500', text: 'text-green-600' }, warning: { color: 'bg-yellow-500', text: 'text-yellow-600' }, error: { color: 'bg-red-500', text: 'text-red-600' } };
  const c = cfg[status] || cfg.healthy;
  return (
    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
      <div className="flex items-center gap-3"><div className={`w-3 h-3 rounded-full ${c.color} animate-pulse`} /><div className="flex-1"><p className="text-slate-900 font-medium">{name}</p><p className="text-xs text-slate-500">{details}</p></div><Badge variant="outline" className={`${c.text} border-current`}>{status}</Badge></div>
    </div>
  );
};

const PlanCard = ({ plan, onEdit, onToggle, onDelete }) => (
  <Card className={`bg-white border-slate-200 shadow-sm ${!plan.is_active && 'opacity-60'}`}>
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between">
        <CardTitle className="text-slate-900 text-lg">{plan.name}</CardTitle>
        <Badge className={plan.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>{plan.is_active ? 'Active' : 'Inactive'}</Badge>
      </div>
      <p className="text-2xl font-bold text-slate-900">${plan.monthly_price}<span className="text-sm text-slate-500 font-normal">/mo</span></p>
    </CardHeader>
    <CardContent className="space-y-2 text-sm text-slate-600">
      <p>• {plan.account_limit} accounts</p>
      <p>• {plan.chatbot_limit} AI agents</p>
      <p>• {plan.message_limit.toLocaleString()} messages/mo</p>
      <p>• {plan.document_limit} documents</p>
      <div className="flex gap-2 pt-4">
        <Button size="sm" variant="outline" onClick={onEdit} className="flex-1 border-slate-300 text-slate-700"><Edit className="w-4 h-4 mr-1" />Edit</Button>
        <Button 
          size="sm" 
          onClick={onToggle} 
          className={plan.is_active 
            ? 'bg-green-600 hover:bg-green-700 text-white' 
            : 'bg-slate-300 hover:bg-slate-400 text-slate-700'
          }
          title={plan.is_active ? 'Click to deactivate' : 'Click to activate'}
        >
          <Power className="w-4 h-4" />
        </Button>
        <Button 
          size="sm" 
          variant="outline"
          onClick={onDelete}
          className="border-red-300 text-red-600 hover:bg-red-50"
          title="Delete plan"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </CardContent>
  </Card>
);


export default AdminPage;
